/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { Badge, badgeVariants } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useEventCategoryOptions } from '@web/src/features/event/use-event-category-options'
import { useTranslation } from '@web/src/lib/intl/react'
import { type VariantProps } from 'class-variance-authority'
import { CircleDollarSignIcon, LockIcon } from 'lucide-react'
import type { EventCategoryEnum, EventVisibility } from 'types/graphql'

type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>['size']>
type LegacyBadgeSize = 'xs' | 'sm' | 'md'
type LegacyBadgeVariant = 'solid' | 'subtle' | 'outline' | 'surface' | 'plain'

interface EventBadgesProps {
  visibility?: EventVisibility
  admission?: boolean
  category?: EventCategoryEnum
  tags?: {
    id: string
    name: string
  }[]
  size?: BadgeSize | LegacyBadgeSize
  wrap?: boolean
  maxHeight?: string
  variant?: VariantProps<typeof badgeVariants>['variant'] | LegacyBadgeVariant
}

const EventBadges = ({ visibility, admission, category, tags, wrap = true, maxHeight }: EventBadgesProps) => {
  const { t } = useTranslation()
  const { labelFor } = useEventCategoryOptions()

  const getVisibilityBadge = (visibilityValue: EventVisibility) => {
    switch (visibilityValue) {
      case 'PRIVATE':
        return (
          <Badge size="lg">
            <LockIcon />
            {t('Private Event')}
          </Badge>
        )

      default:
        return null
    }
  }

  return (
    <div
      className={cn(
        'flex w-full items-center justify-start gap-1.5 py-0',
        wrap ? 'flex-wrap' : 'flex-nowrap',
        maxHeight && 'overflow-hidden'
      )}
      style={maxHeight ? { maxHeight } : undefined}
    >
      {visibility && visibility !== 'PUBLIC' && getVisibilityBadge(visibility)}

      {admission && (
        <Badge size="lg">
          <CircleDollarSignIcon />
          {t('Admission Fee')}
        </Badge>
      )}

      {category && (
        <Badge size="lg" variant="outline">
          {labelFor(category)}
        </Badge>
      )}

      {tags?.map((tag) => (
        <Badge key={tag.id} size="lg" variant="outline">
          {tag.name}
        </Badge>
      ))}
    </div>
  )
}

export default EventBadges
